import React, { useState, useEffect, useCallback } from 'react';

const C = { grn:'#389e0d', red:'#cf1322', blue:'#0050b3', amber:'#ad4e00', gray:'#8c8c8c',
            purple:'#531dab', teal:'#08979c' };
const money = v => v == null ? '—' : (v < 0 ? '-' : '') + '\u20B9' + Math.abs(Math.round(v)).toLocaleString('en-IN');
const pct = (v, d = 1) => v == null ? '—' : (v > 0 ? '+' : '') + v.toFixed(d) + '%';
const pnlColor = v => v == null ? 'var(--t2)' : v > 0 ? C.grn : v < 0 ? C.red : 'var(--t2)';

const box = { background:'var(--bg1)', border:'1px solid var(--bd)', borderRadius:8, padding:16, marginBottom:16 };
const th = { textAlign:'left', padding:'6px 10px', fontSize:11, color:'var(--t2)', fontWeight:600,
             textTransform:'uppercase', letterSpacing:0.3, borderBottom:'1px solid var(--bd)', whiteSpace:'nowrap' };
const td = { padding:'6px 10px', fontSize:13, borderBottom:'1px solid var(--bd)', whiteSpace:'nowrap' };
const rt = { ...td, textAlign:'right' };
const rth = { ...th, textAlign:'right' };

function Metric({ label, value, sub, color }) {
  return (
    <div style={{ background:'var(--bg2, var(--bg1))', border:'1px solid var(--bd)',
                  borderRadius:8, padding:'12px 14px', flex:'1 1 130px', minWidth:130 }}>
      <div style={{ fontSize:11, color:'var(--t2)', textTransform:'uppercase',
                    letterSpacing:0.3, marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:20, fontWeight:600, color: color || 'var(--t1)' }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'var(--t2)', marginTop:2 }}>{sub}</div>}
    </div>
  );
}

function Pill({ text, color }) {
  return <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, whiteSpace:'nowrap',
                        background:color + '18', color, fontWeight:600 }}>{text}</span>;
}

export default function MomentumIndex() {
  const [tab, setTab] = useState('overview');
  const [ov, setOv] = useState(null);
  const [today, setToday] = useState(null);
  const [pos, setPos] = useState(null);
  const [rank, setRank] = useState(null);
  const [dec, setDec] = useState(null);
  const [perf, setPerf] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (what) => {
    setLoading(true); setErr(null);
    try {
      if (what === 'overview') {
        setOv(await (await fetch('/mi/overview')).json());
        setToday(await (await fetch('/mi/today')).json());
      }
      if (what === 'positions') setPos(await (await fetch('/mi/positions')).json());
      if (what === 'ranking') setRank(await (await fetch('/mi/ranking')).json());
      if (what === 'decisions') setDec(await (await fetch('/mi/decisions?days=7')).json());
      if (what === 'performance') setPerf(await (await fetch('/mi/performance')).json());
    } catch (e) { setErr(String(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load('overview'); }, [load]);
  useEffect(() => { if (tab !== 'overview') load(tab); }, [tab, load]);

  const TABS = [['overview','Overview'], ['positions','Positions'], ['ranking','Ranking'],
                ['decisions','Decisions'], ['performance','Performance']];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14, flexWrap:'wrap' }}>
        <h2 style={{ margin:0, fontSize:20 }}>Momentum_Index v1.0</h2>
        {ov && <Pill text={ov.mode === 'live' ? 'LIVE' : 'SHADOW — no real orders'}
                     color={ov.mode === 'live' ? C.grn : C.amber} />}
        <span style={{ fontSize:12, color:'var(--t2)' }}>
          momentum stocks + index trend · {ov ? money(ov.capital) : ''}
        </span>
        <button onClick={() => load(tab)} style={{ marginLeft:'auto', fontSize:12, padding:'4px 12px',
          borderRadius:6, border:'1px solid var(--bd)', background:'var(--bg1)', cursor:'pointer' }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:16, flexWrap:'wrap' }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            fontSize:13, padding:'6px 14px', borderRadius:6, cursor:'pointer',
            border:'1px solid ' + (tab === k ? C.purple : 'var(--bd)'),
            background: tab === k ? C.purple + '12' : 'var(--bg1)',
            color: tab === k ? C.purple : 'var(--t1)', fontWeight: tab === k ? 600 : 400 }}>
            {label}
          </button>
        ))}
      </div>

      {err && <div style={{ ...box, borderColor:C.red, color:C.red }}>Error: {err}</div>}

      {tab === 'overview' && ov && <><Today t={today} /><Overview d={ov} /></>}
      {tab === 'positions' && pos && <Positions d={pos} />}
      {tab === 'ranking' && rank && <Ranking d={rank} />}
      {tab === 'decisions' && dec && <Decisions d={dec} />}
      {tab === 'performance' && perf && <Performance d={perf} />}
      {!ov && !err && <div style={{ color:'var(--t2)', padding:20 }}>Loading…</div>}
    </div>
  );
}


function Today({ t }) {
  if (!t) return null;
  const changed = t.events.some(e => e.flipped) || t.orders > 0;
  return (
    <div style={{ ...box, borderLeft: '3px solid ' + (changed ? C.blue : C.gray) }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline',
                    marginBottom:10, flexWrap:'wrap', gap:8 }}>
        <p style={{ fontSize:16, fontWeight:600, margin:0 }}>What happened today</p>
        <span style={{ fontSize:12, color:'var(--t2)' }}>
          {t.date}
          {t.is_month_end && <strong style={{ color:C.purple }}> · month-end rebalance day</strong>}
        </span>
      </div>

      <p style={{ fontSize:14, margin:'0 0 14px', color: changed ? 'var(--t1)' : 'var(--t2)' }}>
        {t.summary}
      </p>

      {t.events.map((e, i) => (
        <div key={i} style={{ display:'flex', gap:12, padding:'10px 0',
                              borderTop:'1px solid var(--bd)' }}>
          <span style={{ fontSize:11, color:'var(--t2)', minWidth:42, paddingTop:2,
                         fontFamily:'ui-monospace, monospace' }}>{e.time}</span>
          <div style={{ flex:1 }}>
            <p style={{ fontSize:13.5, margin:0, fontWeight: e.flipped ? 600 : 400 }}>
              {e.headline}
            </p>
            <p style={{ fontSize:12.5, color:'var(--t2)', margin:'3px 0 0' }}>{e.detail}</p>
            <p style={{ fontSize:12.5, margin:'4px 0 0',
                        color: e.decision === 'BUY' ? C.grn
                             : e.decision === 'SELL' ? C.red : 'var(--t2)' }}>
              → {e.outcome}
            </p>
          </div>
        </div>
      ))}

      {t.book && (
        <div style={{ borderTop:'1px solid var(--bd)', marginTop:4, paddingTop:10,
                      display:'flex', gap:20, flexWrap:'wrap', fontSize:12.5 }}>
          <span style={{ color:'var(--t2)' }}>Book {money(t.book.equity)}</span>
          <span style={{ color: t.book.change ? pnlColor(t.book.change) : 'var(--t2)' }}>
            {t.book.change === 0 || t.book.change == null ? 'unchanged since yesterday'
              : money(t.book.change) + ' on the day'}
          </span>
          <span style={{ color:'var(--t2)' }}>
            {t.orders === 0 ? 'no orders' : t.orders + ' order(s)'}
          </span>
          {(!t.jobs_ran.index_check || !t.jobs_ran.executor) && (
            <span style={{ color:C.amber }}>
              ⚠ {!t.jobs_ran.index_check ? 'index check did not run' : 'executor did not run'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Overview({ d }) {
  const invested = d.momentum.value + d.etf.value;
  const bar = [
    { label:'Momentum', v:d.momentum.value, c:C.purple },
    { label:'Index ETF', v:d.etf.value, c:C.teal },
    { label:'Cash', v:d.cash, c:C.gray },
  ].filter(x => x.v > 0);
  const tot = bar.reduce((s, x) => s + x.v, 0) || 1;

  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <Metric label="Book value" value={money(d.equity)}
                sub={d.today != null ? money(d.today) + ' today' : 'no prior day yet'}
                color={d.today > 0 ? C.grn : d.today < 0 ? C.red : null} />
        <Metric label="Invested" value={money(invested)}
                sub={((invested / d.equity) * 100).toFixed(0) + '% of book'} />
        <Metric label="Cash" value={money(d.cash)}
                sub={((d.cash / d.equity) * 100).toFixed(0) + '% · liquid'} />
        <Metric label="Drawdown" value={pct(d.drawdown_pct)} sub="limit 20%"
                color={d.drawdown_pct < -15 ? C.red : null} />
        <Metric label="Realised" value={money(d.realised)} sub="closed trades"
                color={pnlColor(d.realised)} />
      </div>

      <div style={box}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:12 }}>Structure</div>
        <div style={{ display:'flex', height:20, borderRadius:4, overflow:'hidden', marginBottom:12 }}>
          {bar.map(b => <div key={b.label} title={b.label}
            style={{ width:(100 * b.v / tot) + '%', background:b.c }} />)}
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            <th style={th}>Sleeve</th><th style={rth}>Held</th><th style={rth}>Target</th>
            <th style={rth}>Gap</th><th style={rth}>P&amp;L</th><th style={rth}>Status</th>
          </tr></thead>
          <tbody>
            {d.targets.map(t => {
              const gap = t.target_value != null ? t.held - t.target_value : null;
              return (
                <tr key={t.sleeve}>
                  <td style={td}>{t.sleeve}{t.n ? ' · ' + t.n : ''}</td>
                  <td style={rt}>{money(t.held)}</td>
                  <td style={rt}>{t.target_pct != null ? t.target_pct + '%' : 'residual'}</td>
                  <td style={{ ...rt, color: gap && gap < -1000 ? C.amber : 'var(--t2)' }}>
                    {gap == null ? '—' : money(gap)}</td>
                  <td style={{ ...rt, color:pnlColor(t.pnl) }}>{t.pnl ? money(t.pnl) : '—'}</td>
                  <td style={rt}>
                    {t.status === 'pending' ? <Pill text="pending" color={C.amber} />
                      : t.status === 'funded' ? <Pill text="funded" color={C.grn} />
                      : <span style={{ fontSize:11, color:'var(--t2)' }}>residual</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={box}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:12 }}>Index trend · daily 15:20 check</div>
        {d.indices.length === 0 && <div style={{ color:'var(--t2)', fontSize:13 }}>No evaluation yet</div>}
        {d.indices.map(i => (
          <div key={i.name} style={{ display:'flex', alignItems:'center', gap:12,
                                     padding:'8px 0', borderBottom:'1px solid var(--bd)' }}>
            <div style={{ width:170, fontSize:13 }}>{i.name}</div>
            <div style={{ fontSize:13, color:'var(--t2)' }}>
              {i.level.toLocaleString('en-IN')} vs {i.ma.toLocaleString('en-IN')} avg
            </div>
            <div style={{ fontSize:13, color:i.gap > 0 ? C.grn : C.red }}>{pct(i.gap, 2)}</div>
            <div style={{ marginLeft:'auto' }}>
              <Pill text={i.state === 'ON' ? 'holding · ' + i.days + 'd' : 'in cash · ' + i.days + 'd'}
                    color={i.state === 'ON' ? C.grn : C.gray} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Positions({ d }) {
  if (!d.n) return <div style={box}>
    <div style={{ fontSize:14 }}>No open positions.</div>
    <div style={{ fontSize:13, color:'var(--t2)', marginTop:6 }}>
      Either the market filter is off, or the sleeve has not been funded yet.
    </div></div>;
  return (
    <div style={box}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
        <span style={{ fontSize:15, fontWeight:600 }}>{d.n} open</span>
        <span style={{ fontSize:14, color:pnlColor(d.total_pnl) }}>{money(d.total_pnl)}</span>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            <th style={th}>Stock</th><th style={th}>Sleeve</th><th style={rth}>Rank</th>
            <th style={rth}>Qty</th><th style={rth}>Entry</th><th style={rth}>LTP</th>
            <th style={rth}>Chg</th><th style={rth}>P&amp;L</th><th style={rth}>Held</th>
          </tr></thead>
          <tbody>
            {d.positions.map(p => (
              <tr key={p.sleeve + p.ticker}>
                <td style={td}>{p.ticker}</td>
                <td style={td}><span style={{ fontSize:11, color:'var(--t2)' }}>{p.sleeve}</span></td>
                <td style={rt}>{p.rank || '—'}</td>
                <td style={rt}>{p.qty}</td>
                <td style={rt}>{p.entry.toFixed(2)}</td>
                <td style={rt}>{p.ltp.toFixed(2)}</td>
                <td style={{ ...rt, color:pnlColor(p.chg_pct) }}>{pct(p.chg_pct)}</td>
                <td style={{ ...rt, color:pnlColor(p.pnl) }}>{money(p.pnl)}</td>
                <td style={rt}>{p.days}d · {p.months}mo</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Ranking({ d }) {
  return (
    <div>
      <div style={{ ...box, borderColor: d.market_on ? C.grn : C.amber }}>
        <div style={{ fontSize:14, marginBottom:4 }}>
          Market filter is <strong style={{ color:d.market_on ? C.grn : C.amber }}>
          {d.market_on ? 'ON' : 'OFF'}</strong>
          {d.level && <> — midcap {Math.round(d.level).toLocaleString('en-IN')} vs 100-day average {Math.round(d.ma).toLocaleString('en-IN')}</>}
        </div>
        <div style={{ fontSize:13, color:'var(--t2)' }}>
          {d.market_on
            ? 'The sleeve holds the top 20 at the next rebalance.'
            : 'The sleeve holds nothing while the filter is off — capital sits in the index sleeve and cash.'}
        </div>
        <div style={{ fontSize:12, color:'var(--t2)', marginTop:8 }}>
          Universe {d.universe} · {d.passed} passed filters · position size {money(d.per_position)}
          {' · skipped: '}{Object.entries(d.skipped).map(([k, v]) => k + ' ' + v).join(', ')}
        </div>
      </div>

      <div style={box}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:10 }}>Current ranking</div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={rth}>#</th><th style={th}>Stock</th><th style={rth}>Score</th>
              <th style={rth}>12m</th><th style={rth}>Vol</th><th style={rth}>Price</th>
              <th style={rth}>Qty</th><th style={rth}></th>
            </tr></thead>
            <tbody>
              {d.top.map(t => (
                <tr key={t.ticker} style={{ opacity: t.rank > 20 ? 0.5 : 1 }}>
                  <td style={rt}>{t.rank}</td>
                  <td style={td}>{t.ticker}</td>
                  <td style={rt}>{t.score}</td>
                  <td style={{ ...rt, color:C.grn }}>{pct(t.r12, 0)}</td>
                  <td style={rt}>{t.vol.toFixed(0)}%</td>
                  <td style={rt}>{t.price.toFixed(2)}</td>
                  <td style={rt}>{t.qty}</td>
                  <td style={rt}>
                    {t.held ? <Pill text="held" color={C.grn} />
                      : t.incoming && d.market_on ? <Pill text="would buy" color={C.blue} /> : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {d.leaving.length > 0 && (
          <div style={{ marginTop:10, fontSize:13 }}>
            Would leave at the next rebalance: <strong>{d.leaving.join(', ')}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

function Decisions({ d }) {
  const acted = d.signals.filter(s => s.action !== 'NONE');
  const quiet = d.signals.filter(s => s.action === 'NONE');
  return (
    <div>
      <div style={box}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:10 }}>Orders</div>
        {!d.orders.length && <div style={{ fontSize:13, color:'var(--t2)' }}>None in the last 7 days.</div>}
        {d.orders.length > 0 && (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={th}>Date</th><th style={th}>Side</th><th style={th}>Stock</th>
              <th style={rth}>Qty</th><th style={rth}>Intended</th><th style={rth}>Fill</th>
              <th style={rth}>Slippage</th><th style={rth}>Status</th>
            </tr></thead>
            <tbody>{d.orders.map((o, i) => (
              <tr key={i}>
                <td style={td}>{o.date}</td>
                <td style={{ ...td, color:o.side === 'BUY' ? C.grn : C.red }}>{o.side}</td>
                <td style={td}>{o.ticker}</td>
                <td style={rt}>{o.qty}</td>
                <td style={rt}>{o.intended.toFixed(2)}</td>
                <td style={rt}>{o.fill ? o.fill.toFixed(2) : '—'}</td>
                <td style={rt}>{o.slippage != null ? pct(o.slippage, 2) : '—'}</td>
                <td style={rt}><Pill text={o.status} color={o.status === 'SHADOW' ? C.amber : C.grn} /></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      <div style={box}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>What the rules decided</div>
        <div style={{ fontSize:12, color:'var(--t2)', marginBottom:10 }}>
          Every evaluation is logged, including the quiet ones — a log that records only action
          cannot tell a quiet day from a job that did not run.
        </div>
        {acted.map((s, i) => (
          <div key={'a' + i} style={{ padding:'8px 0', borderBottom:'1px solid var(--bd)' }}>
            <div style={{ fontSize:13 }}>
              <span style={{ color:'var(--t2)' }}>{s.date}</span>{' · '}
              <strong style={{ color:s.action === 'BUY' ? C.grn : C.red }}>{s.action}</strong>{' '}
              {s.ticker}{s.rank ? ' · rank ' + s.rank : ''}
            </div>
            <div style={{ fontSize:12, color:'var(--t2)', marginTop:2 }}>{s.reason}</div>
          </div>
        ))}
        {quiet.map((s, i) => (
          <div key={'q' + i} style={{ padding:'6px 0', borderBottom:'1px solid var(--bd)', opacity:0.75 }}>
            <div style={{ fontSize:12, color:'var(--t2)' }}>
              {s.date} · no action · {s.ticker} — {s.reason}
            </div>
          </div>
        ))}
        {!d.signals.length && <div style={{ fontSize:13, color:'var(--t2)' }}>Nothing logged yet.</div>}
      </div>
    </div>
  );
}

function Performance({ d }) {
  const c = d.curve;
  const last = c.length ? c[c.length - 1] : null;
  const ret = last ? (last.equity / d.capital - 1) * 100 : 0;
  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <Metric label="Since start" value={pct(ret)} sub={money(last ? last.equity - d.capital : 0)}
                color={pnlColor(ret)} />
        <Metric label="Closed trades" value={d.n_closed} />
        <Metric label="Win rate" value={d.win_rate != null ? d.win_rate.toFixed(0) + '%' : '—'} />
        <Metric label="Realised" value={money(d.realised)} color={pnlColor(d.realised)} />
        <Metric label="Days tracked" value={c.length} />
      </div>
      <div style={box}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:10 }}>Equity</div>
        {c.length < 2
          ? <div style={{ fontSize:13, color:'var(--t2)' }}>Needs more than one day of history.</div>
          : <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={th}>Date</th><th style={rth}>Equity</th><th style={rth}>Momentum</th>
                <th style={rth}>ETF</th><th style={rth}>Cash</th><th style={rth}>Drawdown</th>
              </tr></thead>
              <tbody>{c.slice(-20).reverse().map(r => (
                <tr key={r.d}>
                  <td style={td}>{r.d}</td>
                  <td style={rt}>{money(r.equity)}</td>
                  <td style={rt}>{money(r.mom)}</td>
                  <td style={rt}>{money(r.etf)}</td>
                  <td style={rt}>{money(r.cash)}</td>
                  <td style={{ ...rt, color:r.dd < -10 ? C.red : 'var(--t2)' }}>{pct(r.dd)}</td>
                </tr>
              ))}</tbody>
            </table>}
      </div>
      {d.best.length > 0 && (
        <div style={box}>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:10 }}>Biggest contributors</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={th}>Stock</th><th style={rth}>Entry</th><th style={rth}>Exit</th>
              <th style={rth}>Return</th><th style={rth}>P&amp;L</th><th style={th}>Held</th>
            </tr></thead>
            <tbody>{[...d.best, ...d.worst].map((t, i) => (
              <tr key={i}>
                <td style={td}>{t.ticker}</td>
                <td style={rt}>{t.entry.toFixed(2)}</td>
                <td style={rt}>{t.exit.toFixed(2)}</td>
                <td style={{ ...rt, color:pnlColor(t.ret) }}>{pct(t.ret)}</td>
                <td style={{ ...rt, color:pnlColor(t.pnl) }}>{money(t.pnl)}</td>
                <td style={td}>{t.d0} → {t.d1}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
